import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AlertTriangle, CreditCard, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Outstanding-penalties banner for the worker dashboard.
 * Shows the total amount owed across all pending / payment_pending
 * penalties + a one-click "Settle Now" button that starts a Stripe
 * Checkout session for the oldest outstanding penalty.
 *
 * If multiple penalties exist, settling the oldest first surfaces the
 * banner again on next reload with the next amount — by design, so
 * workers don't accidentally pay everything in one click without seeing
 * each charge.
 */
export default function OutstandingPenaltiesBanner() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/worker/outstanding-penalties');
      setData(res.data);
    } catch {
      // Silent — banner just won't render
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleSettleNow = async () => {
    if (!data?.oldest?.id) return;
    setSettling(true);
    try {
      const res = await api.post(`/penalties/${data.oldest.id}/charge`, {
        originUrl: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Could not start payment');
        setSettling(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start payment');
      setSettling(false);
    }
  };

  if (loading || !data || data.count === 0) return null;

  const total = Number(data.totalOwed || 0);
  const count = data.count;
  const oldestAmount = Number(data.oldest?.amount || 0);
  const isMulti = count > 1;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 p-5 sm:p-6"
      data-testid="worker-outstanding-penalties-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-100 border border-amber-200 shrink-0">
          <AlertTriangle className="h-5 w-5 text-amber-700" strokeWidth={2} />
        </span>

        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">
            Outstanding balance
          </p>
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight tabular-nums mt-0.5">
            £{total.toFixed(2)}
          </h3>
          <p className="text-[13px] text-amber-900/80 mt-1 leading-relaxed">
            {isMulti ? (
              <>
                {count} unpaid penalties on your account.{' '}
                <span className="text-slate-600">Next charge:</span>{' '}
                <span className="font-semibold tabular-nums">£{oldestAmount.toFixed(2)}</span>{' '}
                ({data.oldest?.reason ? truncate(data.oldest.reason, 60) : 'no reason given'}).
              </>
            ) : (
              <>{data.oldest?.reason ? truncate(data.oldest.reason, 120) : 'A penalty was issued on your account.'}</>
            )}
          </p>
        </div>

        <button
          onClick={handleSettleNow}
          disabled={settling}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white px-5 h-11 text-[13px] font-semibold transition-colors shadow-[0_8px_24px_-6px_rgba(218,41,28,0.45)] disabled:opacity-60 shrink-0"
          data-testid="worker-settle-now-btn"
        >
          {settling ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CreditCard className="h-4 w-4" />
          )}
          {isMulti ? `Settle £${oldestAmount.toFixed(2)} now` : `Settle £${total.toFixed(2)} now`}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <p className="mt-3 text-[11.5px] text-amber-900/70 flex items-center gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
        Card details handled by Stripe — never stored on ShiftCover365.
      </p>
    </div>
  );
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
